// src/_app.tsx
import { useState, useEffect, Suspense, lazy } from "react";
import PrivateRoute from "components/Auth/PrivateRoute";
import ProtectedRoute from "components/common/ProtectedRoute";
import { useTranslation } from "react-i18next";
import {
  Switch,
  Link as RouterLink,
  Route,
  Redirect,
  useLocation,
  useHistory,
} from "react-router-dom";
import {
  ListItemIcon,
  ListItemText,
  ListItemButton,
  List,
  AppBar,
  Alert,
  Snackbar,
  Drawer,
  Hidden,
  IconButton,
  MenuItem,
  Typography,
  TextField,
  Box,
  Link,
} from "@mui/material";
import useAppSelector from "hooks/useAppSelector";
import useAppDispatch from "hooks/useAppDispatch";
import { hide } from "store/reducers/notifications.reducer";

// MaterialUI Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import BuilderIcon from "assets/icons/BuilderIcon";
import StrategiesIcon from "assets/icons/StrategiesIcon";
import FolderIcon from "@mui/icons-material/Folder";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AdbIcon from "@mui/icons-material/Adb";

// types
import type { TFunction } from "react-i18next";
import type { ChangeEvent } from "react";

// Assets
import LogoTitle from "assets/img/willybit_logo_white.png";
import Spanish from "assets/icons/Spanish";
import British from "assets/icons/British";

// Components & Views
import useOrientation from "hooks/useOrientation";
import RotateView from "components/common/RotateView";
import Login from "components/Auth/Login";
import customAxios from "lib/axios";
import LoadingShow from "components/common/LoadingShow";

import OpenAccount from "components/TradingRobots/OpenAccount";
import CreateAccount from "components/TradingRobots/CreateAccount";
import LinkAccount from "components/TradingRobots/LinkAccount";
import LinkingProgress from "components/TradingRobots/LinkingProgress";
import ForgotPassword from "views/ForgotPassword";
import ResetPassword from "views/ResetPassword";
import AutoLogin from "views/AutoLogin";

// Pages with lazy import
const TradingRobots = lazy(() => import("views/TradingRobots"));
const Robots = lazy(() => import("views/Robots"));
const MyValidation = lazy(() => import("views/MyValidation"));
const Binance = lazy(() => import("views/Binance"));
const Builder = lazy(() => import("views/Builder"));
const BuilderMobile = lazy(() => import("mobile/BuilderMobile"));
const ValidationMobile = lazy(() => import("mobile/ValidationMobile"));
const Strategies = lazy(() => import("views/Strategies"));
const BalanceChart = lazy(() => import("views/BalanceChart"));
const Validation = lazy(() => import("views/Validation"));
const RobotValidation = lazy(() => import("views/RobotValidation"));
const TradingView = lazy(() => import("views/TradingView"));
const Download = lazy(() => import("views/Download"));
const DownloadRobot = lazy(() => import("views/DownloadRobot"));
// src/_app.tsx (zona de lazy imports)
const Tournament = lazy(() => import("views/Tournament"));
const Optimization = lazy(() => import("views/Optimization"));
const Optimized = lazy(() => import("views/Optimized"));
const OptimizationTrials = lazy(() => import("views/OptimizationTrials"));
const AdvancedReport = lazy(() => import("views/AdvancedReport"));

const drawerWidth = 240;

const links = [
  {
    id: 1,
    to: "/builder",
    active: true,
    name: "bot-creator",
    label: "tabs.botCreator",
    icon: <BuilderIcon />,
    section: "builder",
  },
  {
    id: 3,
    to: "/strategies",
    active: true,
    name: "strategies",
    label: "tabs.strategies",
    icon: <StrategiesIcon />,
    section: "builder",
  },
  {
    id: 2,
    to: "/myvalidation",
    active: true,
    name: "myvalidation",
    label: "tabs.testing",
    icon: <FolderIcon />,
    section: "builder",
  },
  {
    id: 7,
    to: "/robots?scope=user",
    active: true,
    name: "robots",
    label: "tabs.robots",
    icon: <SmartToyIcon />,
    section: "builder",
  },
  {
    id: 8,
    to: "/tradingrobots/main",
    active: true,
    name: "tradingrobots",
    label: "tabs.tradingrobots",
    icon: <AdbIcon />,
    section: "builder",
  },
];

const languages = [
  { value: "es", icon: <Spanish /> },
  { value: "en", icon: <British /> },
];

type DrawerContentProps = {
  t: TFunction<"global">;
  activePage: string;
  betaTester: boolean;
};

const DrawerContent = function ({
  t,
  activePage,
  betaTester,
}: DrawerContentProps) {
  const menuItemSx = {
    minHeight: 0,
    px: "20px",
    py: "12px",
    borderRadius: 0,
    color: "#FFFFFFCC",
    "& .MuiListItemText-primary": {
      fontSize: "15px",
      fontWeight: 400,
      lineHeight: 1.2,
      fontFamily: '"Outfit", sans-serif',
    },
    "&:hover": {
      backgroundColor: "#2A3169",
      color: "#FFFFFF",
      borderLeft: "4px solid #F45C1C",
      pl: "16px",
    },
    "&.MuiListItemButton-root.Mui-selected": {
      color: "#FFFFFF",
      backgroundColor: ["#2A3169", "!important"],
      borderLeft: "4px solid #F45C1C",
      pl: "16px",
      "& .MuiListItemText-primary": {
        fontWeight: 400,
      },
    },
  };

  const firstSectionLinks = links
    .filter((link) => link.active)
    .filter((link) => (link.id === 7 ? betaTester : true))
    .filter((link) => link.section === "builder");

  const isSelected = (to: string, name: string) => {
    if (to.includes("?")) {
      const [path, query] = to.split("?");
      return activePage.startsWith(path) && activePage.includes(query);
    }
    if (name === "robots") {
      return activePage === "/robots";
    }
    return activePage === to;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <List sx={{ border: 0, py: 0.5, "& .MuiListItemButton-root": { my: "5px" } }}>
        <Box px="20px" pt={1} pb={0.5}>
          <Typography sx={{ color: "#FFFFFF80", fontSize: 11, letterSpacing: 1.2 }}>
            {t("tabs.sectionBuilder")}
          </Typography>
        </Box>
        {firstSectionLinks.map((link) => (
          <ListItemButton
            component={RouterLink}
            key={link.id}
            to={link.to}
            selected={isSelected(link.to, link.name)}
            sx={menuItemSx}
          >
            <ListItemText primary={t(link.label)} />
          </ListItemButton>
        ))}
      </List>

      <Box sx={{ mt: "auto", pt: 1.5 }}>
        <List sx={{ border: 0, pb: 1, "& .MuiListItemButton-root": { my: "5px" } }}>
          <ListItemButton
            component="a"
            href="mailto:soporte@willybit.com"
            sx={menuItemSx}
          >
            <ListItemText primary={t("tabs.help")} />
          </ListItemButton>
        </List>
      </Box>
    </Box>
  );
};

const isAuthenticated = () => !!localStorage.getItem("user_id");

const Router = () => {
  const { isDesktop, isPortrait } = useOrientation();
  return (
    <Suspense fallback={<LoadingShow />}>
      <Switch>
        <Route exact path="/" render={() => <Redirect to="/builder" />} />
        <Route path="/dashboard" render={() => <Redirect to="/builder" />} />
        <PrivateRoute path="/tradingrobots/main" authed={isAuthenticated()}>
          <TradingRobots />
        </PrivateRoute>
        <PrivateRoute
          path="/tradingrobots/OpenAccount"
          authed={isAuthenticated()}
        >
          <OpenAccount />
        </PrivateRoute>
        <PrivateRoute
          path="/tradingrobots/CreateAccount"
          authed={isAuthenticated()}
        >
          <CreateAccount />
        </PrivateRoute>
        <PrivateRoute
          path="/tradingrobots/LinkAccount"
          authed={isAuthenticated()}
        >
          <LinkAccount />
        </PrivateRoute>
        <PrivateRoute
          path="/tradingrobots/LinkingProgress"
          authed={isAuthenticated()}
        >
          <LinkingProgress />
        </PrivateRoute>

        <Route
          path="/builder"
          component={
            isDesktop
              ? Builder
              : isPortrait
              ? () => <RotateView title="titles.builder" />
              : BuilderMobile
          }
        />
        <Route path="/myvalidation" component={MyValidation} />
        <Route path="/strategies" component={Strategies} />
        <Route path="/tradingview" component={TradingView} />
        <Route path="/binance" component={Binance} />
        <Route path="/profile" render={() => <Redirect to="/builder" />} />
        <Route path="/download" component={Download} />
        <Route path="/downloadrobot" component={DownloadRobot} />
        <Route path="/robots" component={Robots} />

        <Route path="/news" />
        <Route
          path="/validation"
          component={
            isDesktop
              ? Validation
              : isPortrait
              ? () => <RotateView title="titles.validation" />
              : ValidationMobile
          }
        />
        <Route path="/validation" component={Validation} />
        <Route
          exact
          path={["/generate_balance_chart", "/"]}
          component={BalanceChart}
        />
        <Route path="/RobotValidation" component={RobotValidation} />
        <Route
          path="/validation-report/:id"
          render={() => <AdvancedReport mode="validation" />}
        />
        <Route
          path="/robot-validation-report/:id"
          render={() => <AdvancedReport mode="robotvalidation" />}
        />

        {/* Rutas públicas */}
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/autologin" component={AutoLogin} />

        {/* Ruta torneo */}
        <Route path="/tournament" component={Tournament} />

        {/* Ruta optimization */}
        <Route path="/optimization" component={Optimization} />

        <Route path="/optimized" component={Optimized} />

        <Route path="/optimization-trials" component={OptimizationTrials} />

        <Route path="/myCourses" render={() => <Redirect to="/builder" />} />
      </Switch>
    </Suspense>
  );
};

const App = () => {
  const WILLYBIT_LOGIN_URL = "https://my.willybit.com";
  const [t, i18n] = useTranslation("global");
  const [isUserFree, setIsUserFree] = useState(false);
  const [u] = useTranslation("freemium");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [page, setPage] = useState("/builder");
  const notifications = useAppSelector((state) => state.notifications);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const UrlPath = window.location.pathname.split("/")[1];
  const searchParams = new URLSearchParams(location.search);
  const isWillyBotsScreen =
    UrlPath === "robots" && searchParams.get("scope") === "shared";
  const history = useHistory();

  const from = (location.state as any)?.from as string | undefined;

  // origen guardado (Top10/Trials)
  const optimizationsFromStored =
    sessionStorage.getItem("optimizations_from") || undefined;

  // SOLO queremos flecha si vienes del flujo Top10/Trials
  const showBackFromOptimizations =
    (typeof from === "string" &&
      (from.startsWith("/optimized") ||
        from.startsWith("/optimization-trials"))) ||
    (UrlPath === "builder" &&
      typeof optimizationsFromStored === "string" &&
      (optimizationsFromStored.startsWith("/optimized") ||
        optimizationsFromStored.startsWith("/optimization-trials")));

  const trialsFromStored = sessionStorage.getItem("trials_from") || undefined;

  useEffect(() => {
    // si llegamos a Optimized desde Trials, guardamos esa URL
    if (
      UrlPath === "optimized" &&
      typeof from === "string" &&
      from.startsWith("/optimization-trials")
    ) {
      sessionStorage.setItem("trials_from", from);
    }

    // opcional: si sales del flujo de optimizaciones, limpias
    if (
      UrlPath !== "optimized" &&
      UrlPath !== "builder" &&
      UrlPath !== "validation"
    ) {
      sessionStorage.removeItem("trials_from");
    }
  }, [UrlPath, from]);

  const goBackSmart = () => {
    if (!showBackFromOptimizations) return;

    if (UrlPath === "builder" && optimizationsFromStored) {
      // 👇 marca que vamos a crear un “duplicado” de optimized en el history
      sessionStorage.setItem("optimized_skip_once", "1");

      history.replace(optimizationsFromStored);
      sessionStorage.removeItem("optimizations_from");
      return;
    }

    history.goBack();
  };

  const goBackOptimized = () => {
    const skip = sessionStorage.getItem("optimized_skip_once") === "1";

    if (skip) {
      sessionStorage.removeItem("optimized_skip_once");
      history.go(-2); // ✅ salta el optimized duplicado y cae en trials
      return;
    }

    history.goBack();
  };

  const goBack = () => {
    // si hay historial, vuelve atrás
    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    // fallback si entraste directo por URL
    // (aquí puedes decidir un destino fijo)
    // por ejemplo: volver a strategies
    window.location.href = "/strategies";
  };

  const goStrategies = () => {
    history.replace("/strategies");
  };

  // --- AUTENTICACIÓN + RUTAS PÚBLICAS
  const authed = !!localStorage.getItem("user_id");
  const isPublicRoute =
    location.pathname.includes("forgot-password") ||
    location.pathname.includes("reset-password") ||
    location.pathname.includes("autologin");

  // Mostrar modal de login solo cuando NO hay sesión y NO es ruta pública
  const showLoginModal = false;

  // En Willybit no se permite login local: sin sesión, solo acceso por autologin.
  useEffect(() => {
    if (!authed && !location.pathname.includes("autologin")) {
      window.location.replace(WILLYBIT_LOGIN_URL);
    }
  }, [authed, location.pathname]);

  /* *********************************** // Check if the user is a beta tester //   ******************************************** */
  const [betaTester, setBetaTester] = useState(false);
  const findBetaTester = async () => {
    try {
      const beta = true;
      setBetaTester(beta);
    } catch (error) {
      console.error("Error fetching user data:", error);
      return false;
    }
  };

  useEffect(() => {
    // Si llegamos a BUILDER desde optimized / optimization-trials, guardamos ese "from"
    if (
      UrlPath === "builder" &&
      typeof from === "string" &&
      (from.startsWith("/optimized") || from.startsWith("/optimization-trials"))
    ) {
      sessionStorage.setItem("optimizations_from", from);
    }

    // Si salimos del flujo (ni builder ni validation), limpiamos
    if (UrlPath !== "builder" && UrlPath !== "validation") {
      sessionStorage.removeItem("optimizations_from");
    }
  }, [UrlPath, from]);

  useEffect(() => {
    findBetaTester();
    let actualLocation = `${location.pathname}${location.search || ""}`;
    if (actualLocation === "/") {
      actualLocation = "/builder";
    }
    setPage(actualLocation);
  }, [location, location.pathname]);

  const handleLanguageChange = (event: ChangeEvent<HTMLInputElement>) => {
    i18n.changeLanguage(event.target.value);
  };
  const handleSnackbarClose = () => dispatch(hide());
  const handleDrawerToggle = () => setMobileOpen((s) => !s);

  useEffect(() => {
    let actualLocation = `${location.pathname}${location.search || ""}`;
    if (actualLocation === "/") {
      actualLocation = "/builder";
    }
    setPage(actualLocation);
    return () => {};
  }, [location, location.pathname]);

  // --- EFECTO QUE HACÍA LLAMADAS AL BACK: ahora solo si authed
  useEffect(() => {
    if (!authed) return; // ⛔️ No llames al back antes de login

    const controller = new AbortController();

    async function fetchApi() {
      try {
        const { data } = await customAxios(`/user/validationCount`, {
          signal: controller.signal as any,
        });

        if (
          Number(localStorage.getItem("user_valid_count")) !==
          data.validation_count
        ) {
          localStorage.setItem(
            "user_valid_count",
            String(data.validation_count)
          );
        }
        if (localStorage.getItem("type_user") !== data.type_user) {
          localStorage.setItem("type_user", data.type_user);
          setIsUserFree(data.type_user === "F");
        }
      } catch (err: any) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          // petición cancelada al desmontar
          return;
        }
        localStorage.setItem("user_valid_count", "0");
      }
    }

    fetchApi();
    return () => controller.abort();
  }, [authed]);

  return (
    <>
      {showLoginModal && <Login />}

      {/* Monta el layout + router solo si hay sesión o estás en una ruta pública */}
      {(authed || isPublicRoute) && (
        <Box display="flex" width="100%" height="100%" bgcolor="#E8E8EA">
          {/* Drawer lateral */}
          {authed && (
            <Box
              component="nav"
              sx={{
                width: { md: drawerWidth },
                flexShrink: { md: 0 },
              }}
            >
              <Hidden mdUp implementation="js">
                <Drawer
                  variant="temporary"
                  open={mobileOpen}
                  onClose={handleDrawerToggle}
                  sx={{
                    "& .MuiDrawer-paper": {
                      border: 0,
                      width: drawerWidth,
                      backgroundColor: "#1F2557",
                      color: "#FFFFFFCC",
                      display: "flex",
                      flexDirection: "column",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    },
                  }}
                  ModalProps={{ keepMounted: true }}
                >
                  <Box width="100%" pt={2.5} pb={2.5} display="flex" justifyContent="center">
                    <img src={LogoTitle} width="140px" alt="Willybit Logo" />
                  </Box>
                  <DrawerContent
                    t={t}
                    activePage={page}
                    betaTester={betaTester}
                  />
                </Drawer>
              </Hidden>

              <Hidden mdDown implementation="js">
                <Drawer
                  sx={{
                    "& .MuiDrawer-paper": {
                      overflowX: "hidden",
                      border: 0,
                      width: drawerWidth,
                      backgroundColor: "#1F2557",
                      color: "#FFFFFFCC",
                      display: "flex",
                      flexDirection: "column",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    },
                  }}
                  variant="permanent"
                  open
                >
                  <Box width={drawerWidth} pt={2.5} pb={2.5} display="flex" justifyContent="center">
                    <img src={LogoTitle} width="140px" alt="Willybit Logo" />
                  </Box>
                  <DrawerContent
                    t={t}
                    activePage={page}
                    betaTester={betaTester}
                  />
                </Drawer>
              </Hidden>
            </Box>
          )}

          {/* Contenido principal */}
          <Box
            display="flex"
            flexDirection="column"
            width="100%"
            height="100%"
            overflow="auto"
          >
            {authed && (
              <AppBar
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  py: 0.5,
                  px: 2,
                }}
                position="sticky"
                color="transparent"
                elevation={0}
              >
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ display: { md: "none" } }}
                  size="large"
                >
                  <MenuIcon />
                </IconButton>

                {(UrlPath === "builder" ||
                  (UrlPath === "validation" && typeof from !== "string")) &&
                  showBackFromOptimizations && (
                    <IconButton
                      onClick={goBackSmart}
                      size="medium"
                      sx={{ mr: 1 }}
                      color="primary"
                    >
                      <ArrowBackIcon />
                    </IconButton>
                  )}

                {UrlPath === "validation" && typeof from === "string" && (
                  <IconButton
                    onClick={() => {
                      // ✅ si hay historial real, vuelve sin duplicar entradas
                      if (window.history.length > 1) {
                        history.goBack();
                        return;
                      }
                      // fallback si entraste por URL directa
                      history.replace(from);
                    }}
                    size="medium"
                    sx={{ mr: 1 }}
                    color="primary"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                )}

                {UrlPath === "download" && (
                  <IconButton
                    onClick={() => window.history.back()}
                    size="medium"
                    sx={{ mr: 1 }}
                    color="primary"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                )}
                {UrlPath === "downloadrobot" && (
                  <IconButton
                    onClick={() => window.history.back()}
                    size="medium"
                    sx={{ mr: 1 }}
                    color="primary"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                )}

                {UrlPath === "optimized" && (
                  <IconButton
                    onClick={goBackOptimized}
                    size="medium"
                    sx={{ mr: 1 }}
                    color="primary"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                )}

                {UrlPath === "optimization-trials" && (
                  <IconButton
                    onClick={goStrategies}
                    size="medium"
                    sx={{ mr: 1 }}
                    color="primary"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                )}

                <Typography
                  variant="h5"
                  noWrap
                  sx={{ display: { xs: "none", md: "block" } }}
                  fontFamily="Outfit"
                  fontWeight={500}
                >
                  {isWillyBotsScreen
                    ? t("tabs.willybots")
                    : UrlPath
                    ? t(`titles.${UrlPath}`)
                    : t("titles.builder")}
                </Typography>

                {isUserFree && (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    marginLeft="auto"
                  >
                    <Typography>{u("former")}</Typography>&nbsp;
                    <Link href={u("plans_link")} target="_blank" component="a">
                      {u("improve")}
                    </Link>
                  </Box>
                )}

                <Box
                  marginLeft="auto"
                  display="flex"
                  my={1}
                  alignItems="center"
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    bgcolor="white"
                    borderRadius="5px"
                    mx={1}
                    p={0.5}
                  >
                    <TextField
                      id="language-selector"
                      select
                      onChange={handleLanguageChange}
                      value={i18n.language}
                      sx={{
                        p: 0,
                        "& .MuiOutlinedInput-notchedOutline": { border: 0 },
                        "& .MuiOutlinedInput-input": { p: 0, display: "flex" },
                      }}
                    >
                      {languages.map((language) => (
                        <MenuItem
                          value={language.value}
                          key={language.value}
                          sx={{
                            display: "flex",
                            "& .MuiListItemIcon-root": { minWidth: 0 },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 0 }}>
                            {language.icon}
                          </ListItemIcon>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>

                {localStorage.getItem("type_user") === "F" && (
                  <Box
                    width="100%"
                    sx={{ display: { md: "none" } }}
                    display="block"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      marginLeft="auto"
                      textAlign="center"
                    >
                      <Typography>
                        {t("premiumWarning.former")}&nbsp;
                        <p>{t("premiumWarning.later")}</p>
                      </Typography>
                    </Box>
                  </Box>
                )}
              </AppBar>
            )}

            <Box
              component="main"
              height="100%"
              width="100%"
              display="flex"
              flexDirection="column"
              overflow="auto"
            >
              {/* El Router se renderiza tanto en rutas públicas como con sesión */}
              <Router />
            </Box>
          </Box>
        </Box>
      )}

      <Snackbar
        onClose={handleSnackbarClose}
        autoHideDuration={5000}
        open={notifications.show}
      >
        <Alert
          severity={notifications.type}
          variant="filled"
          onClose={handleSnackbarClose}
        >
          {notifications.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default App;



